// folderService.js
//
// Real API layer for the Trivvy "folders" feature (TBV-726). Replaces the former
// FRONTEND-only mock. A folder is a `type:"folder"` document living in the same
// collection as its items; an item carries a singular `folderId` (an item lives
// in at most one folder — the "move" model). Library folders live in the
// triviaResources collection and use their own endpoints.
//
// State is keyed by category/screen:
//   'SURVEY' | 'KNOWLEDGE_CHECK' | 'MESSAGE' | 'LIBRARY'
//
// Item counts are computed by the API (each folder carries `itemCount`); the FE
// keeps them as a folderId -> itemCount map so the existing `getFolderItemCount`
// call sites keep working unchanged.

import { orderBy } from 'lodash'
import axios from '../../../axios'
import * as url from '../../../utils/url'

// FE category -> BE trivia-list params. LIBRARY is not here (it uses its own
// endpoints). FE 'MESSAGE' maps to BE triviaCategoryType 'MESSAGING' +
// isMessaging:true (the Messages tab keys off isMessaging, not the enum).
const TRIVIA_CATEGORY = {
  SURVEY: { triviaCategoryType: 'SURVEY', isMessaging: false },
  KNOWLEDGE_CHECK: { triviaCategoryType: 'KNOWLEDGE_CHECK', isMessaging: false },
  MESSAGE: { triviaCategoryType: 'MESSAGING', isMessaging: true },
}

const isLibrary = (category) => category === 'LIBRARY'

// Unwrap the totoro formatter envelope: res.formatter.ok(payload) -> data.data.
const payload = (res) => (res && res.data && res.data.data) || {}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

// Folders for a category — each carries an API-computed `itemCount`.
export const getFolders = (category) => {
  if (isLibrary(category)) {
    return axios.get(url.getLibraryFolders).then((res) => payload(res).folders || [])
  }
  const cfg = TRIVIA_CATEGORY[category] || {}
  return axios
    .get(url.getFolders, { params: { triviaCategoryType: cfg.triviaCategoryType } })
    .then((res) => payload(res).folders || [])
}

// Items inside a folder. Fetched on demand — the paginated main list can't be
// relied on to hold every member of a folder.
export const getFolderContents = (category, folderId) => {
  if (isLibrary(category)) {
    return axios
      .get(url.getLibraryFolderContents(folderId))
      .then((res) => payload(res).items || [])
  }
  const cfg = TRIVIA_CATEGORY[category] || {}
  return axios
    .get(url.getFolderContents(folderId), {
      params: { triviaCategoryType: cfg.triviaCategoryType },
    })
    .then((res) => payload(res).items || [])
}

// ---------------------------------------------------------------------------
// Mutations — write-only. Each performs the change and resolves; it does NOT
// re-fetch the folder list. The caller (folderWiring) decides what to refresh:
// the folder LIST only when it's on screen (top level, or on exit from a
// folder), or just the open folder's CONTENTS while inside a folder. This keeps
// get-folders from firing on every in-folder add/remove/rename.
// createFolder still returns { folder } so the caller can drop an item into the
// folder it just created.
// ---------------------------------------------------------------------------

export const createFolder = (category, name) => {
  const cfg = TRIVIA_CATEGORY[category] || {}
  const create = isLibrary(category)
    ? axios.post(url.createLibraryFolder, { name })
    : axios.post(url.createFolder, {
        name,
        triviaCategoryType: cfg.triviaCategoryType,
        isMessaging: cfg.isMessaging,
      })
  return create.then((res) => ({ folder: payload(res).folder || null }))
}

export const renameFolder = (category, folderId, name) => {
  const rename = isLibrary(category)
    ? axios.put(url.renameLibraryFolder(folderId), { name })
    : axios.put(url.renameFolder(folderId), { name })
  return rename.then(() => ({}))
}

export const deleteFolder = (category, folderId) => {
  const del = isLibrary(category)
    ? axios.delete(url.deleteLibraryFolder(folderId))
    : axios.delete(url.deleteFolder(folderId))
  return del.then(() => ({}))
}

export const addItemToFolder = (category, folderId, itemId) => {
  const add = isLibrary(category)
    ? axios.post(url.addLibraryItemToFolder, { itemId, folderId })
    : axios.post(url.addItemToFolder, { itemId, folderId })
  return add.then(() => ({}))
}

// Bulk add: many items into one folder in a SINGLE request (the BE accepts an
// itemIds array). So "add existing" is one write, no matter how many items are
// selected — not one call per item.
export const addItemsToFolder = (category, folderId, itemIds) => {
  const add = isLibrary(category)
    ? axios.post(url.addLibraryItemToFolder, { itemIds, folderId })
    : axios.post(url.addItemToFolder, { itemIds, folderId })
  return add.then(() => ({}))
}

export const removeItemFromFolder = (category, folderId, itemId) => {
  const remove = isLibrary(category)
    ? axios.post(url.removeLibraryItemFromFolder, { itemId, folderId })
    : axios.post(url.removeItemFromFolder, { itemId, folderId })
  return remove.then(() => ({}))
}

// ---------------------------------------------------------------------------
// Pure helpers (no side effects) — safe to call in render.
// ---------------------------------------------------------------------------

// folderId -> itemCount map built from the API folder list. Kept so the existing
// `getFolderItemCount(map, folderId)` call sites in the screens/components keep
// working without change.
export const buildCountMap = (folders) => {
  const map = {}
  ;(folders || []).forEach((f) => {
    if (f && f._id) {
      map[f._id] = f.itemCount || 0
    }
  })
  return map
}

export const getFolderItemCount = (countMap, folderId) => {
  if (!countMap) {
    return 0
  }
  return countMap[folderId] || 0
}

export const sortFolders = (folders, sortBy, sortOrder) => {
  const list = folders ? [...folders] : []
  if (sortBy === 'name') {
    // Name sorts A–Z by default; only an explicit 'desc' reverses it — mirrors
    // how the trivvy lists read sortOrder.
    const nameOrder = sortOrder === 'desc' ? 'desc' : 'asc'
    return orderBy(list, (f) => (f.name ? f.name.toLowerCase() : ''), [nameOrder])
  }
  // Date sort keys on updatedAt (shown in the row as "Updated"), falling back to
  // createdOn — the real BE field names. Defaults to NEWEST-FIRST (desc) so a
  // just-created folder lands at the TOP of the list, matching the trivvy list's
  // own createdDate/desc default; only an explicit 'asc' flips it (TBV-726).
  const dateOrder = sortOrder === 'asc' ? 'asc' : 'desc'
  return orderBy(list, (f) => f.updatedAt || f.createdOn, [dateOrder])
}

// Items in `items` that belong to a folder (by their singular folderId).
export const deriveFolderItems = (items, folderId) =>
  (items || []).filter(
    (item) =>
      item && item.folderId && String(item.folderId) === String(folderId)
  )

// Candidates for the "add existing to folder" picker. Move model (TBV-726): only
// un-foldered items are addable — a foldered item lives in its own folder, and
// moving it between folders is a separate action. Also excludes anything already
// in the open folder (`folderMembers` — authoritative and fresh).
export const deriveAddableItems = (items, folderMembers, folderId) => {
  const memberIds = {}
  ;(folderMembers || []).forEach((m) => {
    if (m && m._id) {
      memberIds[m._id] = true
    }
  })
  return (items || []).filter((item) => {
    if (!item) {
      return false
    }
    if (memberIds[item._id]) {
      return false
    }
    // Move model: an item already in any folder is not an "add existing"
    // candidate (folderId is set → it lives under that folder).
    if (item.folderId) {
      return false
    }
    return true
  })
}

// Plural noun for a folder's item count, keyed by category.
export const getFolderNoun = (category) => {
  const map = {
    SURVEY: 'surveys',
    KNOWLEDGE_CHECK: 'knowledge checks',
    MESSAGE: 'messages',
    LIBRARY: 'documents',
  }
  return map[category] || 'items'
}

// "<n> <noun>", singular-aware ("1 survey", "1 knowledge check").
export const formatItemCount = (count, noun) => {
  const total = count || 0
  const plural = noun || 'items'
  if (total === 1) {
    const singular =
      plural.charAt(plural.length - 1) === 's' ? plural.slice(0, -1) : plural
    return `1 ${singular}`
  }
  return `${total} ${plural}`
}

export default {
  getFolders,
  getFolderContents,
  createFolder,
  renameFolder,
  deleteFolder,
  addItemToFolder,
  addItemsToFolder,
  removeItemFromFolder,
  buildCountMap,
  getFolderItemCount,
  sortFolders,
  deriveFolderItems,
  deriveAddableItems,
  getFolderNoun,
  formatItemCount,
}
